import { useState } from 'react'
import { PencilIcon } from '@heroicons/react/16/solid'
import { useDispatch } from '../../../Flux/index.ts'
import { orderWizardActions as wizard } from '../../../Flux/Slice/OrderWizard/OrderWizardSlice.ts'
import type { DraftState } from '../../../Model/OrderWizard/OrderWizardDraft.ts'

type Field = 'name' | 'description'

/** Title and description of the created order, each behind a pencil; saved through the classic order update. */
export const EditableOrderDetails = ({ draft }: { draft: DraftState }) => {
  const dispatch = useDispatch()
  const [editing, setEditing] = useState<Field | null>(null)
  const [value, setValue] = useState('')

  const start = (field: Field) => {
    setValue(field === 'name' ? draft.name : (draft.description ?? ''))
    setEditing(field)
  }
  const save = () => {
    const name = editing === 'name' ? value : draft.name
    const description = editing === 'description' ? value : draft.description
    if (!name.trim()) {
      return
    }
    dispatch(wizard.orderDetailsSaveRequested({ draftId: draft.id, name, description }))
    setEditing(null)
  }
  const pencil = (field: Field, label: string) => (
    <button
      type="button"
      className="btn btn-ghost btn-xs"
      aria-label={label}
      data-testid={`wizard-edit-${field}`}
      onClick={() => start(field)}
    >
      <PencilIcon className="h-3.5 w-3.5" />
    </button>
  )
  const editor = (multiline: boolean) => (
    <form
      className="flex w-full flex-wrap items-start gap-2"
      onSubmit={(event) => {
        event.preventDefault()
        save()
      }}
    >
      {multiline ? (
        <textarea
          autoFocus
          aria-label="Order description"
          className="textarea textarea-bordered textarea-sm min-h-16 flex-1"
          value={value}
          onChange={(event) => setValue(event.target.value)}
        />
      ) : (
        <input
          autoFocus
          aria-label="Order title"
          className="input input-bordered input-sm flex-1"
          value={value}
          onChange={(event) => setValue(event.target.value)}
          data-testid="wizard-edit-input"
        />
      )}
      <button type="submit" className="btn btn-sm" disabled={editing === 'name' && !value.trim()}>
        Save
      </button>
      <button type="button" className="btn btn-ghost btn-sm" onClick={() => setEditing(null)}>
        Cancel
      </button>
    </form>
  )

  return (
    <div className="min-w-0 flex-1">
      {editing === 'name' ? (
        editor(false)
      ) : (
        <p className="flex min-w-0 items-center gap-1 text-base font-semibold text-slate-900">
          <span className="truncate" data-testid="wizard-order-title">
            {draft.name}
          </span>
          {pencil('name', 'Edit the order title')}
        </p>
      )}
      {editing === 'description' ? (
        editor(true)
      ) : (
        <p className="flex min-w-0 items-center gap-1 text-sm text-slate-600">
          <span className="truncate" title={draft.description ?? undefined}>
            {draft.description?.trim() ? draft.description : <span className="italic text-slate-400">No description</span>}
          </span>
          {pencil('description', 'Edit the order description')}
        </p>
      )}
    </div>
  )
}
